import { DocumentTypes } from '@urbanx/agx.campaigns.client';
import type { Vendor } from '@urbanx/agx.campaigns.client';
import { BeginCampaignState } from '@urbanx/agx.campaigns.client';
import {
  FormSubmissionRequestFormType,
  FormSubmissionRequestState,
} from '@urbanx/agx.campaigns.client';
import {
  PropertyAddress,
  PropertyDetails,
  PropertyImageUrls,
  Form,
  PageStatus,
  AustralianState,
  FormConfigType,
} from '@urbanx/agx-ui-components';
import { AgxApi } from '../../helpers/http';
import { AppDispatch } from '../../store/store';
import { loadFormConfig } from '../../features/form/formReducer';
import { getLatLongForAddress } from '../../utils/getLatLong';
import { v4 as uuid } from 'uuid';
import { SendFormSubmission } from './Types/types';
import { cleanFullAddress } from 'utils/formatAddress';
import { FormType, type CampaignsClient } from '@urbanx/agx.campaigns.client';

export const campaignsApi = (authToken: string) => new AgxApi('campaigns', authToken);

export const beginCampaign = async (
  client: CampaignsClient,
  dispatch: AppDispatch,
  propertyAddress: PropertyAddress,
  formType: FormType,
  state: AustralianState,
  isManualAddress: boolean,
  propertyImageUrls?: PropertyImageUrls,
  propertyDetails?: PropertyDetails,
  vendors?: Vendor[],
  hasPool: boolean = false
) => {
  let details = propertyDetails == null ? null : { ...propertyDetails };

  if (!details?.latitude && !details?.longitude) {
    if (!details) {
      details = {
        parcels: [
          {
            isPrimaryPlan: false,
          },
        ],
        landAreaDetails: {},
      };
    }

    const latLong = await getLatLongForAddress(cleanFullAddress(propertyAddress));

    if (details && latLong?.latitude && latLong?.longitude) {
      details.latitude = latLong.latitude;
      details.longitude = latLong.longitude;
    }
  }

  const formConfig = await client.beginCampaign({
    propertyAddress,
    propertyImageUrls,
    propertyDetails: details,
    isManualAddress,
    formType,
    state: BeginCampaignState[state],
    vendors,
    hasPool,
  });

  dispatch(
    loadFormConfig({
      ...formConfig,
      state: AustralianState[formConfig.state],
      pageStatuses: formConfig.pageStatuses ?? [],
      documents: formConfig.documents.map((document) => ({
        ...document,
        documentType: DocumentTypes[document.documentType],
      })),
      formConfigType: FormConfigType.Campaign,
    })
  );
  return formConfig;
};

export const saveFormCampaign = async (
  client: CampaignsClient,
  campaignId: string,
  formId: string,
  formType: FormType,
  state: AustralianState,
  formData: object | null,
  pageStatuses: PageStatus[] | null
) => {
  const result = await client.saveForm({
    campaignId,
    formId,
    formType: FormSubmissionRequestFormType[formType],
    state: FormSubmissionRequestState[state],
    formData,
    pageStatuses: pageStatuses ?? undefined,
  });
  if (!result.savedSuccessfully) throw new Error('Failed to save form campaign.');
  return true;
};

export const completeCampaignStep = async (
  authToken: string,
  campaignId: string,
  forcedCompletion: boolean = false,
  formId?: string
) => {
  try {
    const { data: form } = await campaignsApi(authToken).post<Form>('CompleteCampaignStep', {
      campaignId,
      formId,
      forcedCompletion,
    });

    return { ...form, formConfigType: FormConfigType.Campaign };
  } catch (err: any) {
    console.error(err);
  }

  return null;
};

export const beginCampaignForm = async (
  client: CampaignsClient,
  campaignId: string,
  formType: FormType,
  state: AustralianState
) => {
  const newFormId = uuid();

  const newFormCreated = await saveFormCampaign(
    client,
    campaignId,
    newFormId,
    formType,
    state,
    null,
    null
  );

  return newFormCreated ? newFormId : null;
};

export const sendForm = async (authToken: string, sendFormSubmission: SendFormSubmission) => {
  await campaignsApi(authToken).post('SendForm', sendFormSubmission);
};
